// Each signature fans out to its own leading-wildcard match in index-service, so both the list and
// each save's change to it are bounded. Saves are also rate-limited per org
// (app:rate_limits:organizationSettings).
export const MAX_DEPENDENCY_SIGNATURES = 1000;
export const MAX_DEPENDENCY_SIGNATURE_LENGTH = 512;

export const TOO_MANY_DEPENDENCY_SIGNATURES_ERROR =
  `An organization can ignore at most ${MAX_DEPENDENCY_SIGNATURES} dependency signatures.`;
export const TOO_MANY_DEPENDENCY_SIGNATURE_CHANGES_ERROR =
  `One save can add or remove at most ${MAX_DEPENDENCY_SIGNATURES} dependency signatures.`;
export const DEPENDENCY_SIGNATURE_TOO_LONG_ERROR =
  `Each dependency signature can be at most ${MAX_DEPENDENCY_SIGNATURE_LENGTH} characters.`;
export const DEPENDENCY_SIGNATURE_CHANGE_TOO_LARGE_ERROR =
  'This save\'s dependency signature changes are too large. Change fewer or shorter signatures per save.';

/**
 * The split DMv3 reads (getDependencySignatures) and the organizations.v1 wire carries. Case and
 * duplicates are kept: consumers lowercase when they match and compare the list as a set.
 */
export const splitDependencySignatures = (raw: string | null): string[] =>
  (raw ?? '').split(',').map((signature) => signature.trim()).filter((signature) => signature !== '');

/** The signatures `after` removes from and adds to `before`, compared as sets, each in stored order. */
export const diffDependencySignatures = (
  before: string | null,
  after: string | null,
): { removed: string[]; added: string[] } => {
  const previous = new Set(splitDependencySignatures(before));
  const next = new Set(splitDependencySignatures(after));
  return {
    removed: [...previous].filter((signature) => !next.has(signature)),
    added: [...next].filter((signature) => !previous.has(signature)),
  };
};

/**
 * Why saving `after` over `before` breaks a bound, or null. The list can't grow past
 * MAX_DEPENDENCY_SIGNATURES and one save can't add or remove more than that many entries, so an
 * org already past the cap can still save, shorten its list, or swap up to the cap at a time.
 */
export const dependencySignaturesEditError = (before: string | null, after: string | null): string | null => {
  const nextCount = splitDependencySignatures(after).length;
  if (nextCount > MAX_DEPENDENCY_SIGNATURES && nextCount > splitDependencySignatures(before).length) {
    return TOO_MANY_DEPENDENCY_SIGNATURES_ERROR;
  }
  const { removed, added } = diffDependencySignatures(before, after);
  if (removed.length > MAX_DEPENDENCY_SIGNATURES || added.length > MAX_DEPENDENCY_SIGNATURES) {
    return TOO_MANY_DEPENDENCY_SIGNATURE_CHANGES_ERROR;
  }
  const isAddingTooLong = added.some((signature) => signature.length > MAX_DEPENDENCY_SIGNATURE_LENGTH);
  return isAddingTooLong ? DEPENDENCY_SIGNATURE_TOO_LONG_ERROR : null;
};
